import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Star, Loader2, Landmark } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/tableau-de-bord", { replace: true }); }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register({ name: form.name, email: form.email, password: form.password, phone: form.phone });
      }
      toast.success("Bienvenue sur LéoParts !");
      navigate("/tableau-de-bord");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/tableau-de-bord";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitText = mode === "login" ? "Se connecter" : "Créer mon compte";

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left visual */}
      <div className="hidden md:flex flex-col justify-between bg-congo-blue p-10 text-white relative overflow-hidden">
        <div className="cursor-pointer" onClick={() => navigate("/")} data-testid="auth-full-logo">
          <img src="/leoparts-full.png" alt="LéoParts Capital Markets" className="h-44 w-auto object-contain -ml-3" />
        </div>
        <div>
          <h2 className="font-head text-3xl font-bold leading-tight">Bâtissez la nation, part après part.</h2>
          <p className="text-blue-200 mt-4 leading-relaxed">Investissez dès 50$ dans les grands projets de la RDC. Fonds sécurisés par la CADECO.</p>
        </div>
        <div className="flex items-center gap-2 text-blue-200 text-sm"><Landmark className="h-4 w-4 text-congo-gold" /> Partenaire bancaire officiel : CADECO</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="md:hidden flex items-center gap-2 mb-8 justify-center" onClick={() => navigate("/")}>
            <div className="h-9 w-9 rounded-xl bg-congo-blue flex items-center justify-center overflow-hidden"><img src="/leoparts-head.png" alt="LéoParts" className="h-7 w-7 object-contain" /></div>
            <span className="font-head font-extrabold text-xl">LéoParts</span>
          </div>
          <h1 className="font-head text-2xl font-bold text-slate-900">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>
          <p className="text-slate-500 text-sm mt-1">{mode === "login" ? "Accédez à votre portefeuille d'investissements." : "Commencez à investir en quelques minutes."}</p>

          <Button onClick={googleLogin} variant="outline" data-testid="google-login-btn"
            className="w-full mt-6 h-12 rounded-full gap-2 border-slate-300">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" /> Continuer avec Google
          </Button>
          <div className="flex items-center gap-3 my-5"><span className="h-px flex-1 bg-slate-200" /><span className="text-xs text-slate-400">ou</span><span className="h-px flex-1 bg-slate-200" /></div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Nom complet</Label>
                <Input id="name" data-testid="input-name" required value={form.name} onChange={set("name")} className="h-12 rounded-lg mt-1" placeholder="Jean Kabila" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="input-email" required value={form.email} onChange={set("email")} className="h-12 rounded-lg mt-1" placeholder="vous@exemple.cd" />
            </div>
            {mode === "register" && (
              <div>
                <Label htmlFor="phone">Téléphone (Mobile Money)</Label>
                <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} className="h-12 rounded-lg mt-1" placeholder="+243 ..." />
              </div>
            )}
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" data-testid="input-password" required value={form.password} onChange={set("password")} className="h-12 rounded-lg mt-1" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn" className="w-full h-12 rounded-full bg-congo-blue hover:bg-congo-bluedark text-base">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : submitText}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === "login" ? "Pas encore de compte ? " : "Déjà inscrit ? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-mode-btn" className="text-congo-blue font-semibold hover:underline">
              {mode === "login" ? "S'inscrire" : "Se connecter"}
            </button>
          </p>
          <p className="text-center text-xs text-slate-400 mt-4">
            En continuant, vous acceptez nos <a href="/conditions" data-testid="auth-cgu-link" className="text-congo-blue hover:underline">Conditions Générales</a> et notre <a href="/confidentialite" data-testid="auth-privacy-link" className="text-congo-blue hover:underline">Politique de Confidentialité</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
